import React, { useState, useEffect, useRef } from 'react';
import { Avatar, Badge, Box, IconButton, InputAdornment, Stack, TextField, Paper, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { useNavigate } from 'react-router-dom';
import {ReactComponent as MessageIcon} from '../../Icons/svg/message.svg';
import { setLocation } from '../../store/slices/LocationSlice';

const DashboardTopBar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user?.user);
  const avatarUrl = user?.profilePhoto?.url;
  
  // Get location from Redux
  const locationFromRedux = useSelector((state: RootState) => state.location?.location);

  // Location search state
  const [searchInput, setSearchInput] = useState('');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Helper function to check if a string is a Plus Code
  const isPlusCode = (address: string): boolean => {
    const plusCodePattern = /^[A-Z0-9]{2,}\+[A-Z0-9]{2,}$/i;
    return plusCodePattern.test(address.trim());
  };

  // Helper function to remove Plus Code from an address string
  const removePlusCode = (address: string): string => {
    // Pattern to match Plus Code at the start of string (with optional comma/space after)
    const plusCodePattern = /^[A-Z0-9]{2,}\+[A-Z0-9]{2,}[\s,]*/i;
    let cleaned = address.replace(plusCodePattern, '').trim();
    
    // Also remove Plus Code if it appears anywhere in the string (with commas/spaces around it)
    cleaned = cleaned.replace(/[\s,]*[A-Z0-9]{2,}\+[A-Z0-9]{2,}[\s,]*/gi, ' ').trim();
    
    // Clean up multiple spaces or commas
    cleaned = cleaned.replace(/[\s,]+/g, ' ').replace(/^[\s,]+|[\s,]+$/g, '');
    
    return cleaned || address; // Return original if cleaning removes everything
  };

  // Helper function to extract a proper address from geocoding results
  const extractAddressFromResults = (results: google.maps.GeocoderResult[]): string => {
    // First, try to find a result with a proper formatted address (not a Plus Code)
    for (const result of results) {
      if (result.formatted_address) {
        const address = result.formatted_address;
        // If it's not just a Plus Code, clean it and return
        if (!isPlusCode(address)) {
          return removePlusCode(address);
        }
      }
    }

    // If all formatted_addresses are Plus Codes, try to use compound_code from plus_code
    for (const result of results) {
      if (result.plus_code?.compound_code) {
        const parts = result.plus_code.compound_code.split(',');
        if (parts.length > 1) {
          // Return location info without the Plus Code
          const address = parts.slice(1).join(',').trim();
          return removePlusCode(address);
        }
      }
    }

    // Fallback: clean the first result's formatted_address
    const fallback = results[0]?.formatted_address || 'Unknown Location';
    return removePlusCode(fallback);
  };

  // Initialize Google Maps services
  useEffect(() => {
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new google.maps.places.AutocompleteService();
    }
    if (!placesService.current && window.google && mapRef.current) {
      const map = new window.google.maps.Map(mapRef.current);
      placesService.current = new google.maps.places.PlacesService(map);
    }
  }, []);

  // Load location from Redux when component mounts
  useEffect(() => {
    if (locationFromRedux?.name) {
      setSearchInput(locationFromRedux.name);
      console.log('Loaded location from Redux:', locationFromRedux);
    }
  }, [locationFromRedux]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);

    if (value && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        { input: value },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setPredictions(predictions);
            setShowDropdown(true);
          } else {
            setPredictions([]);
          }
        }
      );
    } else {
      setPredictions([]);
      setShowDropdown(false);
    }
  };

  const handlePlaceSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    if (placesService.current) {
      placesService.current.getDetails(
        { placeId: prediction.place_id },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place && place.geometry?.location) {
            const locationData = {
              name: place.formatted_address || prediction.description,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
            setSearchInput(locationData.name);
            setShowDropdown(false);
            
            // Dispatch to Redux LocationSlice
            dispatch(setLocation(locationData));
            console.log('Location set in Redux:', locationData);
          }
        }
      );
    }
  };

  const handleGetCurrentLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          
          // Reverse geocode to get place name
          if (window.google) {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
              { location: { lat: latitude, lng: longitude } },
              (results, status) => {
                if (status === 'OK' && results && results.length > 0) {
                  const address = extractAddressFromResults(results);
                  const locationData = {
                    lat: latitude,
                    lng: longitude,
                    name: address,
                  };
                  setSearchInput(address);
                  
                  // Dispatch to Redux LocationSlice
                  dispatch(setLocation(locationData));
                  console.log('Current location set in Redux:', locationData);
                  
                  // Show success toast
                  window.dispatchEvent(
                    new CustomEvent('app-toast', {
                      detail: {
                        message: 'Location updated successfully',
                        type: 'success',
                        duration: 3000,
                      },
                    }),
                  );
                }
                setGettingLocation(false);
              }
            );
          } else {
            const locationData = {
              lat: latitude,
              lng: longitude,
              name: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
            };
            setSearchInput(locationData.name);
            
            // Dispatch to Redux LocationSlice
            dispatch(setLocation(locationData));
            console.log('Current location set in Redux:', locationData);
            setGettingLocation(false);
            
            // Show success toast
            window.dispatchEvent(
              new CustomEvent('app-toast', {
                detail: {
                  message: 'Location updated successfully',
                  type: 'success',
                  duration: 3000,
                },
              }),
            );
          }
          setShowDropdown(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          let errorMessage = 'Unable to get your location. Please check your browser settings.';
          let errorType: 'error' | 'warning' = 'error';
          
          // Provide more specific error messages based on error code
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions in your browser settings.';
              errorType = 'warning';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please try again or search for a location manually.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'Unable to get your location. Please check your browser settings or search for a location manually.';
              break;
          }
          
          // Show error toast with helpful message
          window.dispatchEvent(
            new CustomEvent('app-toast', {
              detail: {
                message: errorMessage,
                type: errorType,
                duration: 5000,
                onClick: () => {
                  // Optionally open browser settings help
                  if (error.code === error.PERMISSION_DENIED) {
                    window.open('https://support.google.com/chrome/answer/114662', '_blank');
                  }
                },
              },
            }),
          );
          setGettingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      window.dispatchEvent(
        new CustomEvent('app-toast', {
          detail: {
            message: 'Geolocation is not supported by your browser. Please search for a location manually.',
            type: 'error',
            duration: 5000,
          },
        }),
      );
      setGettingLocation(false);
    }
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleSearchClick = () => {
    setShowDropdown(true);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ px: 2, py: 1 }}
    >
      {/* User Avatar */}
      <Box onClick={() => navigate('/profile')}>
      <Avatar src={avatarUrl} sx={{ width: 36, height: 36 }} />
      </Box>

      {/* Hidden div for Google Maps service */}
      <div ref={mapRef} style={{ display: 'none' }} />

      {/* Search bar with location autocomplete */}
      <Box sx={{ flexGrow: 1, position: 'relative' }} ref={searchRef}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search location..."
          value={searchInput}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onClick={handleSearchClick}
          sx={{
            '& .MuiOutlinedInput-root': {
              pr: 0.5,
              borderRadius: 99,
              backgroundColor: '#F9F0EB',
              '& fieldset': {
                border: 'none',
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end" size="small" sx={{ mr: 0.5 }}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Dropdown with locations */}
        {showDropdown && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              maxHeight: '400px',
              overflow: 'auto',
              zIndex: 1300,
              boxShadow: 3,
            }}
          >
            <List sx={{ py: 0 }}>
              {/* Get Current Location - Always first */}
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={handleGetCurrentLocation}
                  disabled={gettingLocation}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <MyLocationIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={gettingLocation ? "Getting location..." : "Get current location"}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              </ListItem>

              {/* Google Places Predictions */}
              {predictions.map((prediction) => (
                <ListItem key={prediction.place_id} disablePadding>
                  <ListItemButton onClick={() => handlePlaceSelect(prediction)}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LocationOnIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={prediction.structured_formatting.main_text}
                      secondary={prediction.structured_formatting.secondary_text}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: '0.85rem' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}

              {/* No results message */}
              {searchInput && predictions.length === 0 && !gettingLocation && (
                <ListItem>
                  <ListItemText 
                    primary="No locations found"
                    secondary="Try a different search term"
                    sx={{ textAlign: 'center', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        )}
      </Box>

      {/* Chat Icon with notification dot */}
      <IconButton onClick={() => navigate('/chatlist')} sx={{ ml: 0.5 }} size="small">
        <Badge color="error" variant="dot" overlap="circular">
          <MessageIcon />
        </Badge>
      </IconButton>
    </Stack>
  );
};

export default DashboardTopBar;
